import { type RefObject, useEffect, useRef } from 'react';

import type { Point2, ReadableStore } from '@frontend/viewer-core';

import { mappingRoleDefinition } from '../session/roles';
import { isBrushTool, roleOfBrushTool } from '../session/toolController';
import type {
  MarqueePreview,
  PointerState,
  SelectionManager,
  ToolId,
} from '../session/types';
import { bindOverlayFrame } from '../utils/toolUtils';

/** Offset from the pointer, so the chip trails the cursor rather than sitting under it. */
const BADGE_OFFSET_PX: Point2 = [14, 18];

/**
 * The chip that trails the cursor while a role brush is armed and the pointer
 * is over something paintable — a click or marquee release anywhere else would
 * paint nothing, and the badge says so before the click happens.
 *
 * **Imperative on purpose**, the same reason `useDxfMappingMenuAnchor` is: this
 * has to track every pointer move, and routing that through React state would
 * re-render the mapping chrome at pointer-move rate. Visibility, position,
 * label and color are all written straight to the DOM inside one `draw()`.
 *
 * Positioned from `pointer`, the live tier — not from a hover ref on the
 * session, which does not exist — so paintability is re-derived here via
 * `selection.pickAt`, the same hit test the input controller itself uses.
 */
export const useDxfBrushCursorBadge = ({
  pointer,
  tool,
  selection,
  marqueePreview,
}: {
  pointer: ReadableStore<PointerState>;
  tool: ReadableStore<ToolId>;
  selection: Pick<SelectionManager, 'pickAt'>;
  marqueePreview: ReadableStore<MarqueePreview | null>;
}): RefObject<HTMLDivElement> => {
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const badge = badgeRef.current;
    if (!badge) {
      return undefined;
    }

    const draw = () => {
      const currentTool = tool.getState();
      if (!isBrushTool(currentTool)) {
        badge.style.visibility = 'hidden';
        return;
      }

      // A non-empty paint-marquee counts as "over paintable geometry" even
      // where the pointer itself sits over the box's empty middle — the badge
      // is promising what release will do, not describing what is directly
      // under the cursor.
      const isPainting = (marqueePreview.getState()?.loopIds.length ?? 0) > 0;
      const { screen } = pointer.getState();
      const isOverGeometry =
        !isPainting && screen !== null && selection.pickAt(screen) !== null;

      if (!screen || (!isPainting && !isOverGeometry)) {
        badge.style.visibility = 'hidden';
        return;
      }

      const definition = mappingRoleDefinition(roleOfBrushTool(currentTool));
      badge.textContent = definition?.label ?? '';
      badge.style.background = definition?.stroke.colorHex ?? '';
      badge.style.transform = `translate(${screen[0] + BADGE_OFFSET_PX[0]}px, ${
        screen[1] + BADGE_OFFSET_PX[1]
      }px)`;
      badge.style.visibility = 'visible';
    };

    return bindOverlayFrame({
      draw,
      subscriptions: [
        pointer.subscribe,
        tool.subscribe,
        marqueePreview.subscribe,
      ],
    });
  }, [pointer, tool, selection, marqueePreview]);

  return badgeRef;
};
